import { useEffect, useMemo, useRef, useState, type FC, type RefObject } from "react";

export interface ReportTab {
  id: string;
  label: string;
  badge?: number;
  badgeTitle?: string;
}

const ACTIVE_PROBE_GAP = 48;

const useActiveSection = (ids: string[], barRef: RefObject<HTMLDivElement>) => {
  const [activeId, setActiveId] = useState(ids[0]);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const probe = (barRef.current?.getBoundingClientRect().bottom ?? 0) + ACTIVE_PROBE_GAP;
      const passed = ids.filter(
        id => (document.getElementById(id)?.getBoundingClientRect().top ?? Infinity) <= probe
      );
      setActiveId(passed[passed.length - 1] ?? ids[0]);
    };
    const onScroll = () => {
      if (!frame) {
        frame = requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      cancelAnimationFrame(frame);
    };
  }, [barRef, ids]);

  return [activeId, setActiveId] as const;
};

interface ReportTabBarProps {
  tabs: ReportTab[];
  ariaLabel: string;
}

const ReportTabBar: FC<ReportTabBarProps> = ({ tabs, ariaLabel }) => {
  const ids = useMemo(() => tabs.map(tab => tab.id), [tabs]);
  const barRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useActiveSection(ids, barRef);

  const scrollToSection = (id: string) => {
    setActiveId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="br__tabbar" ref={barRef}>
      <nav className="br__tabs" aria-label={ariaLabel}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            className="br__tab"
            aria-selected={tab.id === activeId}
            aria-current={tab.id === activeId ? "true" : undefined}
            onClick={() => scrollToSection(tab.id)}
          >
            {tab.label}
            {tab.badge ? (
              <span className="br__tab-count" title={tab.badgeTitle}>
                {tab.badge}
              </span>
            ) : null}
          </button>
        ))}
      </nav>
    </div>
  );
};

export default ReportTabBar;
